import React, { ChangeEvent, ReactNode, useEffect, useMemo, useState } from "react";
import { Box, Card, CardContent, FormControl, FormControlLabel, FormLabel, Radio, RadioGroup } from "@mui/material";
import { BlockEntity, ClusterType, LensHit, PageType } from "Backend";
import Caption from "components/Caption";
import { UseFormCard } from "../useFormCard";
import { P } from "theme/typography";
import { ValueGroup } from "components/FormField/ValueGroup";
import { BooleanCell } from "components/FormField/BooleanCell";
import { findLensHitFromBlock } from "redux/slices/intakeViewer";
import { Block, CellKey, FormField } from "generated/graphql";
import Tag from "components/Tag";
import { FormFieldSelect } from "components/FormFieldSelect";
import getFieldCellValues from "./getFieldCellValues";
import { isFormValueAnswered } from "helpers/isFormValueAnswered";
import ValueGroupReferencePagePicker from "components/FormField/ValueGroupReferencePagePicker";
import ValueGroupSelect from "components/FormField/ValueGroupSelect";
import { useAppSelector } from "redux/hooks";
import { selectFormCells } from "redux/slices/form.slice";
import FormCardNotes from "../FormCardNotes";

export interface CardFormFragmentProps {
  useFormCard: UseFormCard;
  compact?: boolean;
  block?: Block;
  clusterType?: ClusterType;
  pageType?: PageType;
  entity?: BlockEntity;
  lensRuleDescription?: string;
  lensRuleSubtype?: string;
  docPageCount: number;
  suggestedFormFieldId?: number;
  showReferenceType: boolean;
  showHighlightedText: boolean;
  actions?: ReactNode;
  saveMultipleReferences?: boolean;
  pageHits?: LensHit[] | undefined;
  showNotes?: boolean;
  dataPendo?: string;
}

/*
States
- lens hit or not: Determine Field: Prepopulate based on lens rule, or select.
- Repeating field? Blank form only
- Non-repeating field
  - Answered? Show answer disabled, with a delete button
  - Not Answered? Show form
 */

const CardFormFragment = (props: CardFormFragmentProps) => {
  const {
    form,
    serverValues,
    formGroupValuesState,
    fieldIdState,
    fieldAnsweredState,
    formModeState,
    selectedValueGroupState,
    pageRangeState,
    intakePage,
    multipleReferencesState,
    notesHandle,
  } = props.useFormCard;
  const page = intakePage;
  const {
    block,
    compact,
    showReferenceType,
    showHighlightedText,
    saveMultipleReferences,
    pageHits,
    showNotes,
    dataPendo,
  } = props;
  const [, setGroup] = formGroupValuesState;
  const [answered, setAnswered] = fieldAnsweredState;
  const [selectedValueGroup, setSelectedValueGroup] = selectedValueGroupState;
  const [fieldId, setFieldId] = fieldIdState;
  const [userManuallySetFieldId, setUserManuallySetFieldId] = useState<boolean>(false);
  const [pageRange, setPageRange] = pageRangeState;
  const referenceTypeState = useState<"page" | "page_range">("page");
  const currentLensHit = useMemo(
    () => (block !== undefined ? findLensHitFromBlock(pageHits || [], block) : undefined),
    [pageHits, block]
  );
  const allHitsOfSameType: LensHit[] = useMemo(() => {
    return pageHits !== undefined
      ? pageHits.filter(
          (hit) =>
            `${hit.ruleSubtype}${hit.ruleDescription}` ===
            `${currentLensHit?.ruleSubtype}${currentLensHit?.ruleDescription}`
        )
      : [];
  }, [currentLensHit, pageHits]);

  const field: FormField | undefined = form?.fields.find((f) => f.id === fieldId);
  const isRepeating = Boolean(field?.repeating);
  const fieldValue = serverValues.find((v) => v.fieldId === field?.id);
  const valueGroups = fieldValue?.valueGroups || [];
  const valueGroup = valueGroups.find((vg) => vg.id === selectedValueGroup);
  const isAnswered = field !== undefined ? isFormValueAnswered(field, fieldValue) : false;
  const isYes = answered === true;
  const formCells = useAppSelector(selectFormCells);
  const dateCellIndex = field?.cells.findIndex((c) => c.key === CellKey.PrimaryDate) ?? -1;
  const lensSubtypeCellIndex = field?.cells.findIndex((c) => c.key === CellKey.LensSubtype) ?? -1;
  const nameCellIndex = field?.cells.findIndex((c) => c.key === CellKey.Name) ?? -1;
  const recordTypeCellIndex = field?.cells.findIndex((c) => c.key === CellKey.RecordType) ?? -1;

  const syncFormWithValues = () => {
    if (!form || !serverValues || !field) {
      return;
    }

    setPageRange(undefined);
    referenceTypeState[1]("page");
    if (field.repeating && field.promptYesNo) {
      setAnswered(true);
    } else {
      setAnswered(fieldValue?.yesNoValue || undefined);
    }

    if (!isRepeating && isAnswered && fieldValue) {
      const existingValueGroup = fieldValue.valueGroups[0];
      const isYesServer = fieldValue.yesNoValue === true;
      if (isYesServer && existingValueGroup) {
        setGroup(existingValueGroup.values);
        notesHandle[1](existingValueGroup.notes || undefined);
        return;
      }
    }

    // User has specified to edit an existing response
    if (selectedValueGroup !== -1 && fieldValue) {
      const existingValueGroup = fieldValue.valueGroups.find((vg) => vg.id === selectedValueGroup);
      const isYesServer = fieldValue.yesNoValue === true;
      if (existingValueGroup) {
        notesHandle[1](existingValueGroup.notes || undefined);
      }
      if (isYesServer && existingValueGroup) {
        setGroup(existingValueGroup.values);
        return;
      }
    }

    if (selectedValueGroup === -1) {
      notesHandle[1]("");
    }

    setGroup(
      getFieldCellValues({
        cells: field.cells,
        block,
        entity: props.entity,
        pageType: props.pageType,
        clusterType: props.clusterType,
        lensRuleDescription: props.lensRuleDescription,
        lensRuleSubtype: props.lensRuleSubtype,
      })
    );
  };

  const changeFieldId = (id: number | undefined) => {
    setFieldId(id);
    setSelectedValueGroup(-1);
    syncFormWithValues();
  };

  const setYesNo = (v: boolean | undefined) => {
    setAnswered(v);
  };

  React.useEffect(() => {
    syncFormWithValues();
  }, [fieldId, selectedValueGroup, formModeState[0]]);

  useEffect(() => {
    if (pageRange === undefined) {
      syncFormWithValues();
    }
  }, [block?.index]);

  useEffect(() => {
    if (props.suggestedFormFieldId !== undefined && props.suggestedFormFieldId !== fieldId && !userManuallySetFieldId) {
      changeFieldId(props.suggestedFormFieldId);
    }
  }, [props.suggestedFormFieldId]);

  if (!form || !serverValues) {
    return <div />;
  }

  const displayValueGroup = field ? field.repeating || !field.promptYesNo || (field.promptYesNo && isYes) : false;
  const displayYesNo = field ? !field.repeating && field.promptYesNo : false;

  return (
    <div className="flex flex-col p-4 h-full overflow-auto min-w-72">
      <P variant="subtitle3">Pin to Profile</P>
      <div className="flex flex-wrap gap-4 my-2">
        <div className="flex items-center gap-2 w-full">
          <FormFieldSelect
            className="w-full"
            dataPendo={dataPendo}
            fieldId={fieldId}
            changeFieldId={(fieldId) => {
              setUserManuallySetFieldId(true);
              changeFieldId(fieldId);
            }}
            noneLabel="No Field Selected"
            descriptionTooltipSx={{ mx: 0 }}
          />
        </div>
        {isRepeating && isAnswered && (
          <ValueGroupSelect
            dateCellIndex={dateCellIndex}
            lensSubtypeCellIndex={lensSubtypeCellIndex}
            nameCellIndex={nameCellIndex}
            recordTypeCellIndex={recordTypeCellIndex}
            label="Action"
            groupId={selectedValueGroup}
            groupResponseNumber={valueGroup?.responseNumber ?? -1}
            setGroupId={setSelectedValueGroup}
            valueGroups={valueGroups}
            formCells={formCells}
            addNewValueGroup
            dataPendo={`${dataPendo}-value-group-select`}
            className="min-w-48 w-full"
          />
        )}
      </div>
      <P variant="subtitle3" className="mb-2">
        Information
      </P>
      {/*<Typography>{field.prompt}</Typography>*/}
      {displayYesNo && <BooleanCell value={answered} setValue={setYesNo} />}

      {displayValueGroup && field && (
        <ValueGroup compact={compact} stateHandle={formGroupValuesState} cells={field.cells} />
      )}

      <Box sx={{ display: "flex", mb: 1, mt: 2 }}>
        <ValueGroupReferencePagePicker
          pageRangeState={pageRangeState}
          page={page}
          docPageCount={props.docPageCount}
          showReferenceType={showReferenceType}
          referenceTypeState={referenceTypeState}
          dataPendo={dataPendo}
        />
        {saveMultipleReferences && block !== undefined && allHitsOfSameType.length > 1 && (
          <FormControl>
            <FormLabel id="save-highlights">Save Highlights</FormLabel>
            <RadioGroup
              value={multipleReferencesState[0]}
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                const multipleHighlightsOption = event.target.value;
                multipleReferencesState[1](multipleHighlightsOption === "true");
              }}
              row
              aria-labelledby="save-highlights"
              name="highlights-group">
              <FormControlLabel value={false} control={<Radio />} label="This Hit" />
              <FormControlLabel
                value={true}
                control={<Radio />}
                label={
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <P>All {allHitsOfSameType.length}</P>
                    <Tag
                      variant="hitDescription"
                      tag={`${currentLensHit?.ruleSubtype} - ${currentLensHit?.ruleDescription}`}
                      sx={{ marginX: 1 }}
                    />
                    <P>Hits</P>
                  </Box>
                }
              />
            </RadioGroup>
          </FormControl>
        )}
      </Box>
      {showNotes && (
        <Card variant="outlined">
          <CardContent>
            <Caption text="Notes" />
            <FormCardNotes notes={notesHandle[0]} setNotes={(_, notes) => notesHandle[1](notes)} />
          </CardContent>
        </Card>
      )}

      <Box sx={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between" }}>
        {block && showHighlightedText && (saveMultipleReferences ? multipleReferencesState[0] !== true : true) && (
          <Box sx={{ display: "flex", flexDirection: "column", mt: 1, minHeight: 20 }}>
            <Caption bold text="Highlighted Text" sx={{ mb: 1 }} />
            <P variant="block12">{block.text}</P>
          </Box>
        )}
        <div></div>
        {props.actions && props.actions}
      </Box>
    </div>
  );
};

export default CardFormFragment;
